import { default as axios, rest } from '@/api/axios';
import { gql } from '../utils';

export default {
  async getPetOwner() {
    try {
      const query = gql`
        query petOwner {
          petOwner {
            id
            email
            firstName
            lastName
            primaryPhone
            locale
            vsLocation
            city {
              id
              country
              baseUrl
              noLicensing
            }
            jurisdiction {
              allowAutoRenewals
            }
            pets {
              slug
            }
            address {
              display
            }
            mailingAddress {
              display
            }
            stripeProfile {
              type
              suffix
              expiryMonth
              expiryYear
            }
            autoRenewals {
              licenses
              tags
            }
          }
        }
      `;

      const { petOwner: results } = await axios.post(`/`, { query });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async setAddresses(home, mailing) {
    try {
      const query = gql`
        mutation setAddresses($home: UserAddressInput!, $mailing: UserAddressInput) {
          setAddresses(home: $home, mailing: $mailing) {
            firstName
            lastName
            addressDisplay
            phone
            birthday {
              day
              month
              year
            }
            uniqueIdentifier
            locale
            pets: getPetsUpForRenewal {
              id
              name
              gender
              size
              type
              breed
              birthday {
                day
                month
                year
              }
              rabiesExpireDate
              hasValidNeuterProof
              hasValidRabiesProof
              hasWaitingRabiesProof
              hasWaitingNeuterProof
              needsRabiesProof
              needsNeuterProof
              images {
                original
              }
              details {
                microchip
                birthday {
                  day
                  month
                  year
                }
                neutered
              }
            }
            address {
              unit
              civic
              street
              cityName
              postal
              province
            }
            mailingAddress {
              unit
              civic
              street
              cityName
              postal
              province
            }
            city {
              baseUrl
              title
              logo {
                original
              }
              licenseByRabies
              tempLicensing
              rabiesRequired
              petOwnerEulaTitle
              licensingDisclaimer
              licensingDisclaimerPart2
              licensingDisclaimerPart3
              addressValidation
              safeAndHappyFund
              verifyVaccinesAndInfoSignup
              petchekEnabled
              requireRabiesProof
              requireNeuterProof
              collectPetOwnerDob
              collectPetOwnerUniqueIdentifier
            }
          }
        }
      `;
      const variables = {
        home,
        mailing,
      };
      const { setAddresses: results } = await axios.post('/', {
        query,
        variables,
      });
      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async addPetOwnerContact(input) {
    try {
      const query = gql`
        mutation addPetOwnerContact($input: PetOwnerContactDetails!) {
          addPetOwnerContact(input: $input) {
            firstName
            lastName
            addressDisplay
            phone
            birthday {
              day
              month
              year
            }
            uniqueIdentifier
            pets: getPetsUpForRenewal {
              id
              name
              gender
              size
              type
              breed
              birthday {
                day
                month
                year
              }
              rabiesExpireDate
              hasValidNeuterProof
              hasValidRabiesProof
              hasWaitingRabiesProof
              hasWaitingNeuterProof
              needsRabiesProof
              needsNeuterProof
              images {
                original
              }
              details {
                microchip
                birthday {
                  day
                  month
                  year
                }
                neutered
              }
            }
            address {
              unit
              civic
              street
              cityName
              postal
              province
            }
            mailingAddress {
              unit
              civic
              street
              cityName
              postal
              province
            }
            locale
            city {
              baseUrl
              title
              logo {
                original
              }
              licenseByRabies
              tempLicensing
              rabiesRequired
              petOwnerEulaTitle
              licensingDisclaimer
              licensingDisclaimerPart2
              licensingDisclaimerPart3
              addressValidation
              safeAndHappyFund
              verifyVaccinesAndInfoSignup
              petchekEnabled
              requireRabiesProof
              requireNeuterProof
              collectPetOwnerDob
              collectPetOwnerUniqueIdentifier
            }
          }
        }
      `;
      const variables = {
        input,
      };

      const { addPetOwnerContact: results } = await axios.post('/', {
        query,
        variables,
      });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async updatePetDetails(details) {
    try {
      const query = gql`
        mutation addPetMedical($details: AddPetMedicalDetails!) {
          addPetMedical(details: $details) {
            id
            name
            gender
            size
            type
            breed
            birthday {
              day
              month
              year
            }
            rabiesExpireDate
            hasValidNeuterProof
            hasValidRabiesProof
            hasWaitingRabiesProof
            hasWaitingNeuterProof
            needsRabiesProof
            needsNeuterProof
            images {
              original
            }
            details {
              microchip
              birthday {
                day
                month
                year
              }
              neutered
            }
          }
        }
      `;
      const variables = {
        details,
      };

      const { addPetMedical: results } = await axios.post('/', {
        query,
        variables,
      });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async uploadRabies(pet, image) {
    const formData = new FormData();
    formData.append('file', image);
    try {
      let response = rest.post(`/api/upload-docs/rabies?pet=${pet}`, formData);
      return response;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async uploadNeuter(pet, image) {
    const formData = new FormData();
    formData.append('file', image);
    try {
      let response = rest.post(`/api/upload-docs/neuter?pet=${pet}`, formData);
      return response;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async getLicenses(id) {
    try {
      const query = gql`
        query license($id: ID!) {
          license(id: $id) {
            id
            name
            price
            expireDate
            expireInterval
          }
        }
      `;
      const variables = {
        id,
      };

      const { license: results } = await axios.post('/', {
        query,
        variables,
      });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async orderLicenses(orderDetails) {
    try {
      const query = gql`
        mutation orderLicense($orderDetails: [OrderDetailsInput!]) {
          orderLicense(orderDetails: $orderDetails) {
            id
          }
        }
      `;

      const variables = {
        orderDetails,
      };

      const { orderLicense: results } = await axios.post('/', {
        query,
        variables,
      });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
};
